import { Injectable, computed, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, fromEvent, interval, merge, of } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import {
  COOKIE_CONSENT_STATUS,
  COOKIE_CONSENT_STORAGE_KEY,
  ONETRUST_ALERT_BOX_CLOSED_COOKIE,
  type CookieConsentStatus,
} from 'src/app/core/constants/cookie-consent.constants';

/**
 * Persists and exposes the user's analytics cookie consent choice.
 *
 * Consent is stored in localStorage so it survives reloads and logout, and is surfaced both as
 * signals (for templates/OnPush) and as {@link status$} (for reactive consumers such as
 * {@link PendoAnalyticsService}). No analytics runs until {@link hasAnalyticsConsent} is `true`.
 *
 * When an external CMP (OneTrust) is configured it becomes the source of truth and calls
 * {@link markExternalCmpActive}, which hides the in-app fallback banner.
 */
@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  /** Current consent status. */
  public readonly status = computed<CookieConsentStatus>(() => this.statusSignal());

  /** `true` only when the user has explicitly granted analytics consent. */
  public readonly hasAnalyticsConsent = computed(
    () => this.statusSignal() === COOKIE_CONSENT_STATUS.GRANTED,
  );

  /**
   * `true` while the in-app fallback banner should show: no external CMP is active and the user
   * has not yet made a choice.
   */
  public readonly isBannerVisible = computed(
    () => !this.externalCmpActiveSignal() && this.statusSignal() === COOKIE_CONSENT_STATUS.UNSET,
  );

  /** Emits the consent status on change; used by reactive analytics gating. */
  public readonly status$: Observable<CookieConsentStatus>;

  private readonly statusSignal = signal<CookieConsentStatus>(this.readPersistedStatus());
  private readonly externalCmpActiveSignal = signal(false);

  constructor() {
    // `toObservable` must run in an injection context (constructor) and after the signal exists.
    this.status$ = toObservable(this.statusSignal);
  }

  /** Marks that an external CMP (OneTrust) manages consent, suppressing the in-app banner. */
  public markExternalCmpActive(): void {
    this.externalCmpActiveSignal.set(true);
  }

  /** Records that the user accepted analytics cookies. */
  public grantConsent(): void {
    this.setStatus(COOKIE_CONSENT_STATUS.GRANTED);
  }

  /** Records that the user declined analytics cookies. */
  public denyConsent(): void {
    this.setStatus(COOKIE_CONSENT_STATUS.DENIED);
  }

  /**
   * Emits once when the user no longer needs to interact with a consent prompt
   * (in-app banner answered or OneTrust banner dismissed).
   */
  public waitForConsentResolved(): Observable<void> {
    if (this.externalCmpActiveSignal()) {
      return this.waitForOneTrustConsentResolved();
    }

    if (this.statusSignal() !== COOKIE_CONSENT_STATUS.UNSET) {
      return of(undefined);
    }

    return this.status$.pipe(
      filter((status) => status !== COOKIE_CONSENT_STATUS.UNSET),
      take(1),
      map(() => undefined),
    );
  }

  private waitForOneTrustConsentResolved(): Observable<void> {
    if (this.isOneTrustBannerDismissed()) {
      return of(undefined);
    }

    return merge(
      fromEvent(globalThis.window, 'OneTrustGroupsUpdated').pipe(take(1)),
      interval(250).pipe(
        filter(() => this.isOneTrustBannerDismissed()),
        take(1),
      ),
    ).pipe(
      take(1),
      map(() => undefined),
    );
  }

  private isOneTrustBannerDismissed(): boolean {
    if (this.statusSignal() !== COOKIE_CONSENT_STATUS.UNSET) {
      return true;
    }

    if (document.cookie.includes(`${ONETRUST_ALERT_BOX_CLOSED_COOKIE}=`)) {
      return true;
    }

    const banner = document.getElementById('onetrust-banner-sdk');
    if (!banner) {
      return false;
    }

    const style = getComputedStyle(banner);
    return (
      banner.style.display === 'none' ||
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      banner.classList.contains('ot-hide')
    );
  }

  private setStatus(status: CookieConsentStatus): void {
    this.statusSignal.set(status);

    try {
      localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, status);
    } catch {
      // Storage can be unavailable (private mode/quota); consent still applies for this session.
    }
  }

  private readPersistedStatus(): CookieConsentStatus {
    try {
      const stored = localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
      if (stored === COOKIE_CONSENT_STATUS.GRANTED || stored === COOKIE_CONSENT_STATUS.DENIED) {
        return stored;
      }
    } catch {
      // Ignore storage read failures and treat as an undecided (unset) state.
    }

    return COOKIE_CONSENT_STATUS.UNSET;
  }
}
